import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { useEffect } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import './App.css';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import ChatArchivePage from './pages/ChatArchivePage';
import ConsentPage from './pages/ConsentPage';
import ProviderConsentPage from './pages/ProviderConsentPage';
import ContentDetailPage from './pages/ContentDetailPage';
import ContentPage from './pages/ContentPage';
import DashboardPage from './pages/DashboardPage';
import GoodToGoPage from './pages/GoodToGoPage';
import HabitDetailPage from './pages/HabitDetailPage';
import HabitsPage from './pages/HabitsPage';
import HomePage from './pages/HomePage';
import JoinPage from './pages/JoinPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import RegisterPage from './pages/RegisterPage';
import SOSPage from './pages/SOSPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import { figmaTheme } from './theme/figmaTheme';

// Reason: gtag('config', ...) in index.html only fires page_view on the
// initial HTML load. React Router navigations are client-side, so we
// emit a page_view event on each location change so GA4's "Pages and
// screens" report reflects every route the user visits.
declare global {
  interface Window { gtag?: (...args: unknown[]) => void; }
}
function GAListener() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag !== 'function') return;
    const path = location.pathname + location.search;
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.origin + path,
      page_title: document.title,
    });
  }, [location]);
  return null;
}

function App() {
  return (
    <ThemeProvider theme={figmaTheme}>
      <CssBaseline />
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <GAListener />
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/consent" element={
                <ProtectedRoute>
                  <ConsentPage />
                </ProtectedRoute>
              } />
              <Route path="/provider-consent" element={
                <ProtectedRoute>
                  <ProviderConsentPage />
                </ProtectedRoute>
              } />

              {/* Protected routes */}
              <Route path="/home" element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              } />

              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />

              <Route path="/chat/:conversationId" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />


              <Route path="/profile" element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } />

              <Route path="/chat-archive" element={
                <ProtectedRoute>
                  <ChatArchivePage />
                </ProtectedRoute>
              } />

              <Route path="/chat-archive/:conversationId" element={
                <ProtectedRoute>
                  <ChatArchivePage />
                </ProtectedRoute>
              } />

              <Route path="/habits" element={
                <ProtectedRoute>
                  <HabitsPage />
                </ProtectedRoute>
              } />

              <Route path="/habits/:habitId" element={
                <ProtectedRoute>
                  <HabitDetailPage />
                </ProtectedRoute>
              } />

              <Route path="/content" element={
                <ProtectedRoute>
                  <ContentPage />
                </ProtectedRoute>
              } />

              <Route path="/content/:contentId" element={
                <ProtectedRoute>
                  <ContentDetailPage />
                </ProtectedRoute>
              } />

              <Route path="/good-to-go" element={
                <ProtectedRoute>
                  <GoodToGoPage />
                </ProtectedRoute>
              } />

              {/* Reason: /join is intentionally NOT wrapped in
                  ProtectedRoute. JoinPage itself redirects unauth
                  visitors to /register?invite=<secret> so the
                  signup flow can carry the invite secret through to
                  the backend (User.signed_up_via_invite_token
                  attribution stamp). Wrapping it would bounce them
                  to /login and lose the secret. */}
              <Route path="/join/:secret" element={<JoinPage />} />

              <Route path="/sos" element={
                <ProtectedRoute>
                  <SOSPage />
                </ProtectedRoute>
              } />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/home" replace />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
